import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as d3 from 'd3';

import formatDate from '../../lib/formatDate';
import formatScore from '../../lib/formatScore';

// import background from '../../assets/images/generic.jpg';
import data from '../../assets/data/ortiz-hrs.json';
import filterSetup from '../../assets/data/filters.json';

const OrtizGraphicContainer = styled.div`
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: relative;
  cursor: crosshair;
`;

const HomerunCount = styled.div`
  position: absolute;
  bottom: 8rem;
  right: 2rem;
  @media (max-width: 600px) {
    bottom: 3rem;
    right: 0rem;
  }
  h6, p {
    text-align: center;
  }
  h6 {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
    text-transform: uppercase;
  }
  p {
    font-size: 2rem;
    color: #d42d24;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: 1px solid #d42d24;
    line-height: 70px;
    margin: 0 auto;

  }
`;

const Tooltip = styled.div`
  background-color: white;
  border: 1px solid rgba(215,215,215);
  width: 150px;
  position: absolute;
  padding: 0.5rem;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  transition: all 0.25s ease-out;
  p {
    font-size: 0.75rem;
    line-height: 0.875rem;
    margin-bottom: 0.25rem;
  }
  `;

const OrtizHomeruns = ({
  width,
  opponentFilter,
  yearFilter,
  seasonTypeFilter,
  seriesTypeFilter,
  venueFilter,
  resultFilter,
  walkoffFilter,
  numberFilter,
  filtersDisabled,
}) => {
  const tooltipRef = useRef(null);

  const [tooltipData, setTooltip] = useState({
    date: '',
    result: '',
    inning: '',
    pitcher: '',
    rbi: '',
    left: 0,
    top: 0,
    visibility: 'no-show',
  });

  const noMatchesRef = useRef(null);
  const height = width;

  const homerunTypes = ['Solo home run', 'Two-run home run', 'Three-run home run', 'Grand slam'];
  const ordinals = ['st', 'nd', 'rd', 'th'];

  let filteredData = [...data];

  let stadiumShortName = '';

  const filterObject = {
    number: numberFilter,
    opponent: opponentFilter,
    year: yearFilter,
    season_type: seasonTypeFilter,
    series_type: seriesTypeFilter,
    venue: venueFilter,
    walkoff: walkoffFilter,
    result: resultFilter,
  };

  const filterKeys = Object.keys(filterObject);

  filterKeys.forEach((f) => {
    if (filterObject[f] !== 'all' && f !== 'number') {
      if (f === 'walkoff') {
        const walkoffValue = filterObject[f].toLowerCase();
        filteredData = filteredData.filter((d) => d[f] === walkoffValue);
      } else {
        filteredData = filteredData.filter((d) => d[f].toString() === filterObject[f]);
      }
    }

    if (f === 'number' && filterObject[f].length > 0) {
      filteredData = filteredData.filter((d) => filterObject[f].includes(d.hr_number));
    }

    if (f === 'venue' && filterObject[f] !== 'all') {
      const selectedVenue = filterSetup.stadium.find((venue) => venue.name === filterObject[f]);
      stadiumShortName = selectedVenue.shortname;
    }
  });

  useEffect(() => {
    if (noMatchesRef.current && filteredData.length === 0) {
      noMatchesRef.current.classList.remove('no-show');
    } else {
      noMatchesRef.current.classList.add('no-show');
    }
  }, [noMatchesRef, filteredData]);

  const xScale = d3.scaleLinear()
    .range([0, width - 10])
    .domain([0, 250]);

  const yScale = d3.scaleLinear()
    .range([0, height - 10])
    .domain([0, 250]);

  const colorScale = [
    'rgba(89,136,157,1)',
    'rgba(157,186,115,1)',
    'rgba(250,186,84,1)',
    'rgba(234,33,45,1)',
  ];

  const mouseEnter = (event, d) => {
    setTooltip({
      ...tooltipData,
      date: formatDate(d.orig_date),
      result: formatScore(d),
      inning: d.inning < 4 ? `${d.inning}${ordinals[d.inning - 1]}` : `${d.inning}th`,
      pitcher: d.pitcher,
      rbi: homerunTypes[d.rbi - 1],
      left: event.offsetX > width / 2 ? event.offsetX - 160 : event.offsetX + 10,
      top: event.offsetY - 10,
      visibility: 'show',
    });

    d3.selectAll('.homerun-circle')
      .attr('fill-opacity', 0.1)
      .attr('stroke-opacity', 0.25);
    d3.selectAll('.homerun-line').attr('opacity', 0.1);
    d3.select(`line[hr_number='${d.hr_number}']`).attr('opacity', 1);
    d3.select(event.target)
      .attr('fill-opacity', 0.5)
      .attr('stroke-opacity', 1);
    d3.select(event.target).raise();
  };

  const mouseLeave = () => {
    setTooltip({
      date: '',
      result: '',
      inning: '',
      pitcher: '',
      rbi: '',
      visibility: 'no-show',
    });

    d3.selectAll('.homerun-circle')
      .attr('fill-opacity', 0.25)
      .attr('stroke-opacity', 1);
    d3.selectAll('.homerun-line').attr('opacity', 0.5);
  };

  return (
    <OrtizGraphicContainer id="ortiz-graphic-container" className={stadiumShortName}>
      <div id="hr__no-matches" className="no-show" ref={noMatchesRef}>
        <p className="benton-n4">There are no home runs matching your selection.</p>
      </div>
      <svg
        width={width}
        height={width}
      >
        <g
          width={width - 10}
          height={width - 10}
          transform="translate(5, 5)"
        >
          {filteredData.map((d) => (
            <line
              className="homerun-line"
              x1={xScale(125)}
              y1={yScale(200)}
              x2={xScale(d.hit_x)}
              y2={yScale(d.hit_y)}
              stroke={colorScale[d.rbi - 1]}
              strokeWidth={0.25}
              opacity={0.5}
              key={`homerun-line-${d.hr_number}-${d.orig_date}`}
              hr_number={d.hr_number}
            />
          ))}

          {filteredData.map((d) => (
            <circle
              className="homerun-circle"
              cx={xScale(d.hit_x)}
              cy={yScale(d.hit_y)}
              r={4}
              fill={colorScale[d.rbi - 1]}
              fillOpacity={0.5}
              strokeWidth={0.25}
              stroke={colorScale[d.rbi - 1]}
              hr_number={d.hr_number}
              key={`homerun-circle-${d.hr_number}-${d.orig_date}`}
              onMouseOver={() => mouseEnter(event, d)}
              onMouseLeave={() => mouseLeave()}
            />
          ))}

        </g>
      </svg>

      <Tooltip
        ref={tooltipRef}
        style={{ left: tooltipData.left, top: tooltipData.top }}
        className={tooltipData.visibility}
      >
        <p className="benton-n4"><strong>{tooltipData.date}</strong></p>
        <p className="benton-n4">{tooltipData.result}</p>
        <p className="benton-n4">{tooltipData.rbi} in the {tooltipData.inning} inning off of {tooltipData.pitcher}.</p>
      </Tooltip>
      {filtersDisabled === false
        && (
          <HomerunCount>
            <h6 className="bentonsanscond-n4">Total home runs</h6>
            <p className="bentonsanscond-n6">{filteredData.length}</p>
          </HomerunCount>
        )}
    </OrtizGraphicContainer>
  );
};

export default OrtizHomeruns;

OrtizHomeruns.propTypes = {
  width: PropTypes.number.isRequired,
  numberFilter: PropTypes.arrayOf(PropTypes.number).isRequired,
  opponentFilter: PropTypes.string.isRequired,
  yearFilter: PropTypes.string.isRequired,
  seasonTypeFilter: PropTypes.string.isRequired,
  seriesTypeFilter: PropTypes.string.isRequired,
  venueFilter: PropTypes.string.isRequired,
  walkoffFilter: PropTypes.string.isRequired,
  resultFilter: PropTypes.string.isRequired,
  filtersDisabled: PropTypes.bool.isRequired,
};
